/**
 * Application Constants
 * 
 * Centralized location for all application-wide constant values
 */

/**
 * Authentication & Storage Keys
 */
export const StorageKeys = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  THEME: 'theme',
  USER_PREFERENCES: 'userPreferences'
} as const;

/**
 * API Configuration
 */
export const ApiConfig = {
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000 // 1 second
} as const;

/**
 * Pagination Defaults
 */
export const PaginationDefaults = {
  PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [5, 10, 25, 50, 100],
  INITIAL_PAGE: 0
} as const;

/**
 * Date/Time Formats
 */
export const DateFormats = {
  SHORT_DATE: 'MM/dd/yyyy',
  LONG_DATE: 'MMMM d, yyyy',
  DATE_TIME: 'MM/dd/yyyy HH:mm',
  TIME_ONLY: 'HH:mm:ss',
  ISO_DATE: 'yyyy-MM-dd'
} as const;

/**
 * Form Validation Constants
 */
export const ValidationRules = {
  PASSWORD_MIN_LENGTH: 6,
  EMAIL_PATTERN: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  USERNAME_MIN_LENGTH: 3,
  USERNAME_MAX_LENGTH: 50
} as const;

/**
 * Dashboard Time Periods
 */
export const TimePeriods = {
  THIS_MONTH: 'This Month',
  LAST_MONTH: 'Last Month',
  LAST_3_MONTHS: 'Last 3 Months',
  THIS_YEAR: 'This Year'
} as const;

/**
 * Animation Durations (in milliseconds)
 */
export const AnimationDurations = {
  FAST: 150,
  DEFAULT: 200,
  SLOW: 300,
  ENTER: 200,
  LEAVE: 150
} as const;

/**
 * Default Messages
 */
export const Messages = {
  ERROR: {
    GENERIC: 'An error occurred. Please try again.',
    NETWORK: 'Network error. Please check your connection.',
    UNAUTHORIZED: 'You are not authorized to perform this action.',
    SESSION_EXPIRED: 'Your session has expired. Please log in again.',
    FORM_INVALID: 'Please fix the errors in the form.',
    LOGIN_FAILED: 'Invalid credentials. Please check your email and password.',
    /** Same text as backend AccountNotFoundException.MESSAGE (login) */
    ACCOUNT_NOT_FOUND: "Account doesn't exist. Please sign up.",
    /** Forgot-password when email is not registered */
    FORGOT_PASSWORD_ACCOUNT_NOT_FOUND:
      'No account found for this email address. Please check the email or sign up for a new account.',
    /** App connection duplicate package/bundle */
    APP_ALREADY_CONNECTED: 'This app is already connected on the platform.',
    /** Same text as backend EmailNotVerifiedException.MESSAGE */
    EMAIL_NOT_VERIFIED: 'Please verify your email before logging in.'
  },
  SUCCESS: {
    SAVED: 'Changes saved successfully.',
    DELETED: 'Item deleted successfully.',
    CREATED: 'Item created successfully.',
    UPDATED: 'Item updated successfully.'
  },
  CONFIRMATION: {
    DELETE: 'Are you sure you want to delete this item?',
    UNSAVED_CHANGES: 'You have unsaved changes. Are you sure you want to leave?'
  }
} as const;

/**
 * Feature Flags (for gradual rollout/testing)
 */
export const FeatureFlags = {
  ENABLE_DARK_MODE: true,
  ENABLE_NOTIFICATIONS: true,
  ENABLE_ANALYTICS: true,
  ENABLE_CHATBOT: false, // Coming soon
  /** When false, /subscription and /subscription/payment are blocked; sidebar shows Manage Plan as coming soon */
  MANAGE_PLAN_PAGE_ENABLED: false
} as const;

/** Customer-facing copy for the Manage Plan (subscription) area */
export const ManagePlanCopy = {
  COMING_SOON: 'Manage Plan is coming soon'
} as const;

/** Spotlight onboarding tour when the user has no connected apps */
export const NoAppsOnboardingTour = {
  GOT_IT: 'Got it',
  NEXT: 'Next',
  SKIP: 'Skip for now',
  STEPS: [
    {
      target: 'configuration',
      title: 'Open Configurations',
      description:
        'Use the sidebar to open your workspace configurations and manage integrations.',
      expandConfiguration: false
    },
    {
      target: 'app-connections',
      title: 'Go to App Connections',
      description:
        'Inside configurations, open App Connections to manage your store integrations.',
      expandConfiguration: true
    },
    {
      target: 'new-connection',
      title: 'Create a New Connection',
      description:
        "Click \u2018New Connection\u2019 and follow the setup flow to connect your first app.",
      navigateTo: '/configuration/app-connections'
    }
  ]
} as const;

/** Page banner shown on analytics pages while no apps are connected */
export const NoAppsConnectedBannerCopy = {
  TITLE: 'Connect Your First App',
  MESSAGE:
    'To start syncing data and using platform features, you\u2019ll need to connect at least one application.',
  BADGE: 'Setup takes less than 2 minutes',
  CTA: 'Go to App Connections',
  FOLLOW_GUIDE: 'Follow the guided steps highlighted on screen to get started.',
  RESTART_GUIDE: 'Restart the guide'
} as const;

/** "How to" guidance shown on the new connection form, one entry per form section */
export const NewConnectionHowTo = {
  APP_DETAILS: {
    title: 'How to fill in App Details',
    message:
      'App Name\nUse the exact name from your store listing — Google Play Console: App content > Store listing > App name. Apple App Store Connect: App Information > Name.\n\n' +
      'Package Name (Android)\nGoogle Play Console > your app > App content > App access, or check your project’s build.gradle file under applicationId. Format: com.company.appname.\n\n' +
      'Bundle ID (iOS)\nApp Store Connect > your app > General > App Information > Bundle ID. Format: com.company.appname.\n\n' +
      'Google Play Store URL\nOpen your app’s Play Store listing and copy the page URL from your browser’s address bar.\n\n' +
      'Apple App Store URL\nOpen your app’s App Store listing and copy the page URL from your browser’s address bar.\n\n' +
      'Industry/Category\nChoose whichever category best matches your app — it’s only used to organize your dashboard.'
  },
  REVIEWS: {
    title: 'How to connect Google Play reviews',
    message:
      'Google Play Service Account Key\n1. In Google Cloud Console, go to IAM & Admin > Service Accounts and create (or pick) a service account.\n' +
      '2. Open it > Keys tab > Add Key > Create new key > JSON, then download the file.\n' +
      '3. In Google Play Console, go to Setup > API access, link the Cloud project, and grant the service account access to this app.\n' +
      'Paste the full contents of the downloaded JSON file above.\n\n' +
      'Google Cloud Project ID\nShown on the Google Cloud Console dashboard next to the project name, or in the JSON key file under "project_id".\n\n' +
      'Google Play Developer ID\nGoogle Play Console > Settings (gear icon) > Developer account > Account details — listed as the Developer ID.'
  },
  APP_HEALTH: {
    title: 'How to connect app health (Firebase)',
    message:
      'Firebase Project ID\nFirebase Console > Project settings (gear icon) > General tab > Project ID.\n\n' +
      'GA4 Property ID\nGoogle Analytics > Admin (gear icon) > Property column > Property details — the numeric Property ID near the top.\n\n' +
      'Android/iOS GA4 Stream ID\nGoogle Analytics > Admin > Data Streams > select the Android or iOS stream — the Stream ID is shown at the top of that stream’s details page.\n\n' +
      'Firebase Service Account Key\nFirebase Console > Project settings > Service accounts tab > Generate new private key, then paste the contents of the downloaded JSON file above.'
  }
} as const;

/** Customer-facing copy for help menu items that are not yet available */
export const HelpMenuCopy = {
  CHATBOT: 'Chatbot is coming soon',
  TUTORIALS: 'Tutorials are coming soon',
  FAQ: 'FAQ is coming soon'
} as const;

/**
 * Route Paths
 */
export const RoutePaths = {
  LOGIN: '/',
  SIGNUP: '/signup',
  VERIFY_EMAIL: '/verify-email',
  MAGIC_LINK: '/magic-link',
  COMPLETE_PROFILE: '/complete-profile',
  DASHBOARD: '/dashboard',
  APP_REVIEWS: '/app-reviews',
  /** @deprecated Summary hidden; redirects to app-reviews */
  REVIEW_SUMMARY: '/app-reviews',
  REPLY_TO_REVIEWS: '/app-reviews',
  APP_HEALTH: '/app-health',
  COMPARE: '/compare',
  SUBSCRIPTION: '/subscription',
  USER_MANAGEMENT: '/user-management',
  CONFIGURATION: '/configuration',
  APP_CONNECTIONS: '/configuration/app-connections',
  REPLY_TEMPLATES: '/configuration/reply-templates',
  TAGS: '/configuration/tags',
  REPORTS: '/reports',
  AUDIT_LOG: '/audit-log',
  PROFILE: '/profile',
  NOTIFICATIONS: '/notifications'
} as const;

/**
 * HTTP Status Codes
 */
export const HttpStatus = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503
} as const;

/**
 * Notification Types
 */
export const NotificationTypes = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
} as const;

// Export types for type safety
export type StorageKeyType = keyof typeof StorageKeys;
export type TimePeriodType = typeof TimePeriods[keyof typeof TimePeriods];
export type RoutePathType = typeof RoutePaths[keyof typeof RoutePaths];
export type HttpStatusType = typeof HttpStatus[keyof typeof HttpStatus];
export type NotificationTypeType = typeof NotificationTypes[keyof typeof NotificationTypes];






